import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, COLOR_PALETTE, baseChartOptions } from '../../shared/echarts-config';
import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import type { FpCategorySeries } from '../../../../types/analytics.types';

type TrendingCategoriesChartProps = {
  dates: string[];
  series: FpCategorySeries[];
};

const TITLE = 'Trending Categories';
const SUBTITLE = 'Search volume by service category over time';

const buildOptions = (dates: string[], series: FpCategorySeries[]) => ({
  ...baseChartOptions,
  tooltip: { trigger: 'axis' as const },
  legend: {
    bottom: 0,
    icon: 'circle',
    textStyle: { color: '#374151', fontSize: 12 },
    data: series.map((s) => s.categoryName),
  },
  grid: { left: '3%', right: '4%', bottom: 44, top: 12, containLabel: true },
  xAxis: {
    type: 'category' as const,
    data: dates,
    axisLabel: {
      formatter: (value: string) => {
        const d = new Date(value);
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      },
      fontSize: 12,
      color: '#6b7280',
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    minInterval: 1,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: series.map((s, i) => ({
    name: s.categoryName,
    type: 'line',
    data: s.counts,
    smooth: true,
    symbol: 'circle',
    symbolSize: 5,
    lineStyle: { width: 2, color: COLOR_PALETTE[i % COLOR_PALETTE.length] },
    itemStyle: { color: COLOR_PALETTE[i % COLOR_PALETTE.length] },
  })),
});

export const TrendingCategoriesChart = ({ dates, series }: TrendingCategoriesChartProps) => {
  const isEmpty =
    series.length === 0 ||
    dates.length === 0 ||
    series.every((s) => s.counts.every((c) => c === 0));

  if (isEmpty) {
    return (
      <ChartContainer title={TITLE} subtitle={SUBTITLE}>
        <EmptyState message="No category search trends for the selected filters." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={TITLE} subtitle={SUBTITLE}>
      <ReactEChartsCore
        echarts={echarts}
        option={buildOptions(dates, series)}
        style={{ height: 320 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
